'use client';

import { ArrowDownIcon, ArrowDownThinIcon } from '@repo/icons';
import { ArrowUpIcon } from '@repo/icons';
import { Button } from '@repo/ui/button';
import { NoData } from '@repo/ui/no-data';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/table';
import { cn } from '@repo/utils/cn';
import { type Row, type Table as TanStackTable, flexRender } from '@tanstack/react-table';
import { useTranslations } from 'next-intl';
import React from 'react';
import TableSkeleton from '@/components/common/skeletons/table-skeleton';
import TableRowComponent from '@/components/common/table/table-row';
import PaginationControls from './pagination-controls';

// ==================== TYPES ====================
interface TableViewProps<T> {
  table: TanStackTable<T>;
  isLoading?: boolean;
  skeletonRows?: number;
  className?: string;
  headerClassName?: string;
  rowClassName?: string | ((rowIndex: number) => string);
  cellClassName?: string;
  wrapperClassName?: string;
  tableBodyClassName?: string;
  showPaginationControls?: boolean;
  renderSubComponent?: (props: { row: Row<T>; isEvenRow: boolean }) => React.ReactElement;
  isLiveMode?: boolean;
}

interface TableHeaderProps {
  table: TanStackTable<any>;
  headerClassName?: string;
  cellClassName?: string;
  renderSubComponent?: (props: { row: Row<any>; isEvenRow: boolean }) => React.ReactElement;
}

interface TableContentProps<T> {
  table: TanStackTable<T>;
  isLoading?: boolean;
  skeletonRows: number;
  rowClassName?: string | ((rowIndex: number) => string);
  cellClassName?: string;
  tableBodyClassName?: string;
  renderSubComponent?: (props: { row: Row<T>; isEvenRow: boolean }) => React.ReactElement;
  isLiveMode?: boolean;
}

// ==================== SUBCOMPONENTS ====================
// Memoize TableHeaderComponent since headers rarely change
const TableHeaderComponent = React.memo<TableHeaderProps>(
  ({ table, headerClassName, cellClassName, renderSubComponent }) => (
    <TableHeader className={cn('rounded py-1.5', headerClassName)}>
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow
          key={headerGroup.id}
          className={cn('border-none vt-text-size-small hover:bg-transparent sm:vt-text-size-small', headerClassName)}
        >
          {headerGroup.headers.map((header, index) => (
            <TableHead
              key={header.id}
              className={cn(
                header.column.getCanSort() && 'cursor-pointer hover:underline',
                'h-2 px-2.5 leading-3 vt-text-size-small vt-body-text-darker sm:vt-text-size-small-sm',
                '!overflow-visible !whitespace-normal !break-words',
                {
                  'rounded-l': index === 0,
                  'rounded-r': index === headerGroup.headers.length - 1,
                },
                cellClassName
              )}
              onClick={header.column.getToggleSortingHandler()}
            >
              {header.isPlaceholder ? null : (
                <div className={cn('flex items-center gap-2')}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{
                    asc: <ArrowUpIcon className="h-4 w-4" />,
                    desc: <ArrowDownIcon className="h-4 w-4" />,
                  }[header.column.getIsSorted() as string] ?? null}
                </div>
              )}
            </TableHead>
          ))}
          {!!renderSubComponent && <div className="flex h-full w-[38px] items-center" />}
        </TableRow>
      ))}
    </TableHeader>
  )
);

TableHeaderComponent.displayName = 'TableHeaderComponent';

// Memoize ExpandableRowButton since it's a simple, stable component
const ExpandableRowButton = React.memo<{ row: Row<any> }>(({ row }) => (
  <Button
    onClick={row.getToggleExpandedHandler()}
    variant="grey"
    className="flex h-auto items-center border-none p-3 hover:!bg-transparent"
  >
    <ArrowDownThinIcon className={row.getIsExpanded() ? 'rotate-180' : ''} />
  </Button>
));

ExpandableRowButton.displayName = 'ExpandableRowButton';

// Keep TableRowWithCells unmemoized since it changes frequently with data
const TableRowWithCells: React.FC<{
  row: Row<any>;
  rowIndex: number;
  rowClassName?: string | ((rowIndex: number) => string);
  cellClassName?: string;
  renderSubComponent?: (props: { row: Row<any>; isEvenRow: boolean }) => React.ReactElement;
  isLiveMode?: boolean;
}> = ({ row, rowIndex, rowClassName, cellClassName, renderSubComponent, isLiveMode }) => {
  const computedRowClass = typeof rowClassName === 'function' ? rowClassName(rowIndex) : rowClassName;
  const isEvenRow = rowIndex % 2 === 0;

  return (
    <React.Fragment key={row.id}>
      <TableRowComponent
        isLiveMode={isLiveMode}
        key={`row-${row.id}`}
        className={cn(
          'overflow-x-visible rounded-lg',
          isEvenRow && 'vt-containers-settings-box-normal-bg',
          computedRowClass,
          row.getIsExpanded() ? 'mb-[1px] rounded-b-none border-b-0' : 'mb-2 last:mb-0'
        )}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(
              'leading-2 min-h-14 overflow-hidden whitespace-normal break-words p-2.5 font-medium',
              cellClassName
            )}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
        {!!renderSubComponent && <ExpandableRowButton row={row} />}
      </TableRowComponent>
      {row.getIsExpanded() && !!renderSubComponent && renderSubComponent({ row, isEvenRow })}
    </React.Fragment>
  );
};

// Keep TableContent unmemoized since it changes frequently with data updates
const TableContent: React.FC<TableContentProps<any>> = ({
  table,
  isLoading,
  skeletonRows,
  rowClassName,
  cellClassName,
  tableBodyClassName,
  renderSubComponent,
  isLiveMode,
}) => {
  const t = useTranslations('common');

  if (isLoading) {
    return <TableSkeleton rows={skeletonRows} />;
  }

  if (table.getRowModel().rows?.length === 0 && !isLiveMode) {
    return (
      <NoData
        title={t('no-data-title')}
        subTitle={t('no-data-subtitle')}
        className="h-52"
        data-testid="no-data"
      />
    );
  }

  return (
    <TableBody
      className={cn(
        'vt-font-highlight-font-family vt-text-size-medium vt-body-text-default vt-containers-settings-box-light-bg sm:vt-text-size-medium-sm',
        tableBodyClassName
      )}
    >
      {table.getRowModel().rows.map((row, rowIndex) => (
        <TableRowWithCells
          key={row.id}
          row={row}
          rowIndex={rowIndex}
          rowClassName={rowClassName}
          cellClassName={cellClassName}
          renderSubComponent={renderSubComponent}
          isLiveMode={isLiveMode}
        />
      ))}
    </TableBody>
  );
};

// ==================== MAIN COMPONENT ====================
const TableView = <T,>({
  table,
  isLoading = false,
  skeletonRows = 5,
  className,
  headerClassName,
  rowClassName,
  cellClassName,
  wrapperClassName,
  tableBodyClassName,
  showPaginationControls = false,
  renderSubComponent,
  isLiveMode = false,
}: TableViewProps<T>) => {
  return (
    <Table
      className={cn('space-y-2 overflow-hidden p-2.5', className)}
      wrapperClassName={wrapperClassName}
    >
      <TableHeaderComponent
        table={table}
        headerClassName={headerClassName}
        cellClassName={cellClassName}
        renderSubComponent={renderSubComponent}
      />

      <TableContent
        table={table}
        isLoading={isLoading}
        skeletonRows={skeletonRows}
        rowClassName={rowClassName}
        cellClassName={cellClassName}
        tableBodyClassName={tableBodyClassName}
        renderSubComponent={renderSubComponent}
        isLiveMode={isLiveMode}
      />

      {showPaginationControls && (
        <PaginationControls
          table={table}
          className="p-4"
          isLoading={isLoading}
        />
      )}
    </Table>
  );
};

export default TableView;
